import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { useT } from "../i18n";

export const Testemunhos = () => {
  const { t } = useT();
  const TESTIMONIALS = t("testemunhos.items");

  return (
    <section
      id="testemunhos"
      data-testid="testimonials-section"
      className="relative py-24 md:py-36 bg-[#0F1A17] overflow-hidden"
    >
      {/* Subtle ambient glow */}
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(163,189,152,0.08),transparent_60%)] pointer-events-none"
      />

      <div className="relative max-w-[1200px] mx-auto px-5 md:px-6">
        {/* ═══ Header ═══ */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 md:mb-24 items-end">
          <div className="lg:col-span-9">
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-[#A3BD98]/70" />
              <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#A3BD98]/80">
                {t("testemunhos.eyebrow")}
              </span>
            </div>
            <h2 className="font-serif text-[32px] md:text-[44px] lg:text-[52px] leading-[1.12] tracking-tight text-[#FEFEFE]">
              <span className="block font-semibold">
                {t("testemunhos.titleLine1")}
              </span>
              <span className="block font-light text-[#FEFEFE]/65 mt-1">
                {t("testemunhos.titleLine2")}
              </span>
              <span className="block font-light text-[#FEFEFE]/65">
                {t("testemunhos.titleLine3Start")}
                <span className="font-medium text-[#FEFEFE]">
                  {t("testemunhos.titleLine3Brand")}
                </span>
                .
              </span>
            </h2>
          </div>
        </div>

        {/* ═══ Cards ═══ */}
        <div className="flex lg:grid lg:grid-cols-3 gap-6 md:gap-7 overflow-x-auto snap-x snap-mandatory lg:overflow-visible -mx-5 md:-mx-6 px-5 md:px-6 pb-2 lg:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {TESTIMONIALS.map((tm, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              data-testid={`testimonial-${i}`}
              className="group relative shrink-0 lg:shrink w-[85%] sm:w-[70%] lg:w-auto snap-start bg-[#FEFEFE] rounded-[14px] p-8 md:p-10 flex flex-col overflow-hidden shadow-[0_10px_25px_-15px_rgba(0,0,0,0.3)] transition-[transform,box-shadow] duration-300 ease-out hover:-translate-y-[6px] hover:shadow-[0_24px_50px_-20px_rgba(0,0,0,0.45)]"
            >
              <span
                aria-hidden
                className="absolute inset-0 rounded-[14px] ring-1 ring-inset ring-transparent group-hover:ring-[#A3BD98]/30 transition-colors duration-300 ease-out pointer-events-none"
              />
              <span
                aria-hidden
                className="absolute top-6 right-7 font-serif font-bold text-[80px] leading-none text-[#A3BD98]/40 group-hover:text-[#A3BD98]/70 transition-colors duration-300 ease-out select-none pointer-events-none"
              >
                “
              </span>

              <div
                className="flex items-center gap-[3px] text-[#EE8984]/85 group-hover:text-[#EE8984] transition-colors duration-300 ease-out mb-7"
                aria-label={t("testemunhos.starsAria")}
              >
                {[...Array(5)].map((_, s) => (
                  <Star key={s} size={13} className="fill-current" />
                ))}
              </div>

              <blockquote className="relative z-10 font-serif text-[19px] md:text-[21px] leading-[1.55] text-[#2A322C] font-normal flex-1">
                {tm.quote}
              </blockquote>

              <figcaption className="mt-10 pt-6 border-t border-[#E4E9DE]/70">
                <div className="font-medium text-[15px] text-[#2A322C]">
                  {tm.author}
                </div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#ADBDAD] mt-1.5 font-semibold">
                  {tm.role}
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        {/* ═══ Social proof bar ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          data-testid="testimonials-social-proof"
          className="mt-14 md:mt-20 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 text-center"
        >
          <div className="flex items-center gap-1.5 text-[#EE8984]">
            {[...Array(5)].map((_, s) => (
              <Star key={s} size={14} className="fill-current" />
            ))}
          </div>
          <span className="hidden sm:inline h-3 w-px bg-white/15" />
          <p className="text-sm md:text-[15px] text-[#FEFEFE]/70 font-light">
            {t("testemunhos.socialPrefix")}
            <span className="text-[#FEFEFE] font-semibold">
              {t("testemunhos.socialReviews")}
            </span>
            {t("testemunhos.socialMiddle")}
            <span className="text-[#FEFEFE] font-semibold">
              {t("testemunhos.socialStars")}
            </span>
            {t("testemunhos.socialEnd")}
          </p>
        </motion.div>
      </div>
    </section>
  );
};
